import { useMemo } from 'react';

import { DeduplicationInfoDialog } from './deduplication-info-dialog';
import formatDate from '../../../../../../../helpers/formatDate';
import { MatterDocFilterOptions, MatterDocsFilters } from '@/../../backend/src/types/types';
import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';
import { DateRangeFilter } from '@/new/components/date-range-filter';
import { ServerFilterMultiSelect } from '@/new/components/filter/server-filter-multi-select';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import { PopoverContent } from '@/new/components/shadcn/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';

interface FilterPopoverProps {
  serverFilters?: MatterDocsFilters;
  onServerFiltersChange?: (filters: MatterDocsFilters) => void;
  // Filter options from the database (all possible values for the matter)
  filterOptions?: MatterDocFilterOptions;
  // Relativity field filters
  relativityFields?: FilterField[];
  relativityDistinctValues?: Record<string, string[]>;
  // Wexler field filters
  wexlerFields?: FilterField[];
  wexlerDistinctValues?: Record<string, string[]>;
  hasDeduplicatedDocs?: boolean;
}

export function FilterPopover({
  serverFilters,
  onServerFiltersChange,
  filterOptions,
  relativityFields,
  relativityDistinctValues,
  wexlerFields,
  wexlerDistinctValues,
  hasDeduplicatedDocs,
}: FilterPopoverProps) {
  // Extract date values from server filters
  const startDate = serverFilters?.startDate ? new Date(serverFilters.startDate) : null;
  const endDate = serverFilters?.endDate ? new Date(serverFilters.endDate) : null;

  // Update date filter through server filter API
  const setStartDate = (date: Date | null) => {
    if (onServerFiltersChange && serverFilters) {
      onServerFiltersChange({
        ...serverFilters,
        startDate: date?.toISOString() || undefined,
      });
    }
  };

  const setEndDate = (date: Date | null) => {
    if (onServerFiltersChange && serverFilters) {
      onServerFiltersChange({
        ...serverFilters,
        endDate: date?.toISOString() || undefined,
      });
    }
  };

  // Get filter options from props (fetched from database)
  const fileTypes = filterOptions?.extensions ?? [];
  const docTypes = filterOptions?.types ?? [];
  const issues = filterOptions?.issues ?? [];
  const persons = filterOptions?.persons ?? [];
  const runs = filterOptions?.runs ?? [];
  // Get selected values from server filters
  const selectedTypes = serverFilters?.documentTypes ?? [];
  const selectedExtensions = serverFilters?.extensions ?? [];
  const selectedIssues = serverFilters?.documentThemes ?? [];
  const selectedPersonIds = serverFilters?.personIds ?? [];
  const selectedRunIds = serverFilters?.runIds ?? [];

  // Wexler choice fields - only fields that have distinctValues (availableOptions)
  const wexlerChoiceFields = useMemo(() => {
    if (!wexlerFields || !wexlerDistinctValues) return [];
    return wexlerFields.filter(
      (field) => wexlerDistinctValues[field.fieldName] && wexlerDistinctValues[field.fieldName].length > 0,
    );
  }, [wexlerFields, wexlerDistinctValues]);

  // Relativity choice fields - only fields that have distinctValues (availableOptions)
  const relativityChoiceFields = useMemo(() => {
    if (!relativityFields || !relativityDistinctValues) return [];
    return relativityFields.filter(
      (field) => relativityDistinctValues[field.fieldName] && relativityDistinctValues[field.fieldName].length > 0,
    );
  }, [relativityFields, relativityDistinctValues]);

  const hasRelativityChoiceFields = relativityChoiceFields.length > 0;

  // Regular filters content (using server-side filters)
  const regularFiltersContent = (
    <>
      <div className="mt-4">
        <p className="my-3 text-gray-600">File Extension</p>
        <ServerFilterMultiSelect
          values={selectedExtensions}
          options={fileTypes}
          onChange={(values) => {
            if (onServerFiltersChange && serverFilters) {
              onServerFiltersChange({ ...serverFilters, extensions: values });
            }
          }}
          placeholder="Select file extensions..."
        />
      </div>

      <div className="mt-4">
        <p className="my-3 text-gray-600">Document Type</p>
        <ServerFilterMultiSelect
          values={selectedTypes}
          options={docTypes}
          onChange={(values) => {
            if (onServerFiltersChange && serverFilters) {
              onServerFiltersChange({ ...serverFilters, documentTypes: values });
            }
          }}
          placeholder="Select document types..."
        />
      </div>

      <div className="mt-4">
        <p className="my-3 text-gray-600">Document Issues</p>
        <ServerFilterMultiSelect
          values={selectedIssues}
          options={issues}
          onChange={(values) => {
            if (onServerFiltersChange && serverFilters) {
              onServerFiltersChange({ ...serverFilters, documentThemes: values });
            }
          }}
          placeholder="Select document issues..."
        />
      </div>

      {/* Entities Filter */}
      <div className="mt-4">
        <p className="my-3 text-gray-600">Entities</p>
        <MultiSelect
          values={selectedPersonIds}
          onValuesChange={(values) => {
            if (onServerFiltersChange && serverFilters) {
              onServerFiltersChange({ ...serverFilters, personIds: values.length > 0 ? values : undefined });
            }
          }}
        >
          <MultiSelectTrigger className="w-full">
            <MultiSelectValue placeholder="Select entities..." />
          </MultiSelectTrigger>
          <MultiSelectContent>
            <MultiSelectGroup>
              {persons.map((person) => (
                <MultiSelectItem key={person.id} value={person.id}>
                  {person.name}
                </MultiSelectItem>
              ))}
            </MultiSelectGroup>
          </MultiSelectContent>
        </MultiSelect>
      </div>

      {/* Run Ids Filter */}
      <div className="mt-4">
        <div className="my-3 flex items-center justify-between">
          <p className="text-center text-gray-600">Processing Run</p>
          {/* Include Deduplicated Documents checkbox - only shown when runs are selected and there are duplicates */}
          {selectedRunIds.length > 0 && hasDeduplicatedDocs && (
            <div className="animate-fadeIn flex items-center gap-2">
              <Checkbox
                id="include-deduplicated"
                checked={serverFilters?.includeDeduplicatedDocs ?? true}
                onCheckedChange={(checked) => {
                  if (onServerFiltersChange && serverFilters) {
                    onServerFiltersChange({
                      ...serverFilters,
                      includeDeduplicatedDocs: checked === true,
                    });
                  }
                }}
              />
              <label htmlFor="include-deduplicated" className="text-xs text-gray-600">
                Include deduplicated documents
              </label>
              <DeduplicationInfoDialog />
            </div>
          )}
        </div>

        <MultiSelect
          values={selectedRunIds}
          onValuesChange={(values) => {
            if (onServerFiltersChange && serverFilters) {
              onServerFiltersChange({
                ...serverFilters,
                runIds: values.length > 0 ? values : undefined,
                includeDeduplicatedDocs: values.length > 0 ? true : undefined,
              });
            }
          }}
        >
          <MultiSelectTrigger className="w-full">
            <MultiSelectValue placeholder="Select run..." />
          </MultiSelectTrigger>
          <MultiSelectContent>
            <MultiSelectGroup>
              {runs.map((run) => (
                <MultiSelectItem key={run.id} value={run.id}>
                  {formatDate(run.createdDate, true, true)}
                </MultiSelectItem>
              ))}
            </MultiSelectGroup>
          </MultiSelectContent>
        </MultiSelect>
      </div>

      {/* Wexler Metadata Fields - shown in Regular Filters */}
      {wexlerChoiceFields.map((field) => {
        const options = wexlerDistinctValues?.[field.fieldName] ?? [];
        const currentFilter = serverFilters?.metadataFilters?.find((f) => f.field === field.fieldName);
        const selectedValues = (
          Array.isArray(currentFilter?.value)
            ? currentFilter.value
            : currentFilter?.value !== undefined
              ? [currentFilter.value]
              : []
        ) as string[];

        return (
          <div key={field.fieldName} className="mt-4">
            <p className="my-3 text-gray-600">{field.displayName}</p>
            <ServerFilterMultiSelect
              values={selectedValues}
              options={options}
              onChange={(values) => {
                if (onServerFiltersChange && serverFilters) {
                  const currentMetadataFilters = serverFilters.metadataFilters ?? [];
                  const filteredMetadataFilters = currentMetadataFilters.filter((f) => f.field !== field.fieldName);
                  const newMetadataFilters =
                    values && values.length > 0
                      ? [...filteredMetadataFilters, { field: field.fieldName, value: values }]
                      : filteredMetadataFilters;
                  onServerFiltersChange({
                    ...serverFilters,
                    metadataFilters: newMetadataFilters.length > 0 ? newMetadataFilters : undefined,
                  });
                }
              }}
              placeholder={`Select ${field.displayName.toLowerCase()}...`}
            />
          </div>
        );
      })}

      {/* Document Date Range - moved to end */}
      <div className="mt-4">
        <p className="my-3 text-gray-600">Document Date Range</p>
        <DateRangeFilter startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      </div>
    </>
  );

  // Relativity filters content
  const relativityFiltersContent = (
    <>
      {relativityChoiceFields.map((field) => {
        const options = relativityDistinctValues?.[field.fieldName] ?? [];
        const currentFilter = serverFilters?.metadataFilters?.find((f) => f.field === field.fieldName);
        const selectedValues = (
          Array.isArray(currentFilter?.value)
            ? currentFilter.value
            : currentFilter?.value !== undefined
              ? [currentFilter.value]
              : []
        ) as string[];

        return (
          <div key={field.fieldName} className="mt-4">
            <div className="mb-3 flex items-center gap-2">
              <Icon className="lucide--database h-3.5 w-3.5 text-purple-600" />
              <p className="text-gray-600">{field.displayName}</p>
            </div>
            <ServerFilterMultiSelect
              values={selectedValues}
              options={options}
              onChange={(values) => {
                if (onServerFiltersChange && serverFilters) {
                  const currentMetadataFilters = serverFilters.metadataFilters ?? [];
                  const filteredMetadataFilters = currentMetadataFilters.filter((f) => f.field !== field.fieldName);
                  const newMetadataFilters =
                    values && values.length > 0
                      ? [...filteredMetadataFilters, { field: field.fieldName, value: values }]
                      : filteredMetadataFilters;
                  onServerFiltersChange({
                    ...serverFilters,
                    metadataFilters: newMetadataFilters.length > 0 ? newMetadataFilters : undefined,
                  });
                }
              }}
              placeholder={`Select ${field.displayName.toLowerCase()}...`}
            />
          </div>
        );
      })}
      {relativityChoiceFields.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">No Relativity choice fields available for filtering.</p>
      )}
    </>
  );

  return (
    <PopoverContent side="left" className="mt-12 min-w-md" sideOffset={8}>
      {hasRelativityChoiceFields ? (
        // Tabbed layout when Relativity choice fields exist
        <Tabs defaultValue="regular" className="w-full">
          <div className="-mx-4 -mt-4 rounded-t-md border-b bg-gray-50 px-4 pt-4">
            <TabsList className="w-full justify-start bg-transparent">
              <TabsTrigger value="regular">Regular Filters</TabsTrigger>
              <TabsTrigger value="relativity" className="flex items-center gap-1.5">
                <Icon className="lucide--database h-3.5 w-3.5 text-purple-600" />
                Relativity Filters
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="regular" className="max-h-96 overflow-y-auto">
            {regularFiltersContent}
          </TabsContent>

          <TabsContent value="relativity" className="max-h-96 overflow-y-auto">
            {relativityFiltersContent}
          </TabsContent>
        </Tabs>
      ) : (
        // Flat layout when no Relativity choice fields
        <>
          <div className="-mx-4 -mt-4 rounded-t-md border-b bg-gray-50 p-4 font-medium text-gray-700">Filters</div>
          <div className="max-h-96 overflow-y-auto px-2">{regularFiltersContent}</div>
        </>
      )}

      <div className="mt-4 flex w-full justify-end border-t pt-4">
        <Button
          onClick={() => {
            if (onServerFiltersChange) {
              onServerFiltersChange({});
            }
          }}
        >
          <Icon className="lucide--funnel-x" />
          <span>Clear All Filters</span>
        </Button>
      </div>
    </PopoverContent>
  );
}
